import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../api/client';
import Button from '../../components/Button';
import Badge from '../../components/Badge';
import FormField, { Input, Select } from '../../components/FormField';
import SeatMapMarkerEl, { MARKER_CFG } from '../../components/SeatMapMarker';

const STATUS_BG     = { free: '#d1fae5', reserved: '#fef3c7', booked: '#dbeafe', blocked: '#fee2e2' };
const STATUS_BORDER = { free: '#34d399', reserved: '#fbbf24', booked: '#60a5fa', blocked: '#f87171' };
const STATUS_TEXT   = { free: '#065f46', reserved: '#92400e', booked: '#1e3a8a', blocked: '#991b1b' };

const MARKER_TYPES = Object.entries(MARKER_CFG).map(([k, v]) => ({ value: k, label: `${v.icon} ${v.label}` }));

export default function AdminSeats() {
  const [events, setEvents]               = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [seats, setSeats]                 = useState([]);
  const [markers, setMarkers]             = useState([]);
  const [form, setForm]                   = useState({ label: '', pos_x: 0, pos_y: 0, row_name: '', seat_number: '' });
  const [addError, setAddError]           = useState('');
  const [bulkRows, setBulkRows]           = useState('');
  const [bulkCols, setBulkCols]           = useState('');
  const [bulkSpacing, setBulkSpacing]     = useState('60');
  const [markerForm, setMarkerForm]       = useState({ type: 'eingang', label: '', width: '', height: '' });
  const [activeTab, setActiveTab]         = useState('seats');

  const [selectedIds, setSelectedIds]     = useState(new Set());
  const dragState                         = useRef(null);
  const [isDragging, setIsDragging]       = useState(false);
  const [reassign, setReassign]           = useState(null); // { seat } – Quelle
  const [reassignTarget, setReassignTarget] = useState('');
  const [reassigning, setReassigning]     = useState(false);

  const [tickets, setTickets]             = useState([]);
  const [assign, setAssign]               = useState(null); // freier Sitz, der zugewiesen werden soll
  const [assignTarget, setAssignTarget]   = useState('');
  const [assigning, setAssigning]         = useState(false);

  useEffect(() => { api.get('/api/events/all').then((r) => setEvents(r.data)); }, []);

  const loadSeats = useCallback(() => {
    if (!selectedEvent) return;
    api.get(`/api/events/${selectedEvent}/seats`).then((r) => { setSeats(r.data); setSelectedIds(new Set()); });
  }, [selectedEvent]);

  const loadMarkers = useCallback(() => {
    if (!selectedEvent) return;
    api.get(`/api/events/${selectedEvent}/markers`).then((r) => setMarkers(r.data));
  }, [selectedEvent]);

  const loadTickets = useCallback(() => {
    if (!selectedEvent) return;
    api.get(`/api/admin/tickets?event_id=${selectedEvent}`).then((r) => setTickets(r.data));
  }, [selectedEvent]);

  useEffect(() => { loadSeats(); loadMarkers(); loadTickets(); }, [loadSeats, loadMarkers, loadTickets]);

  const ticketsWithoutSeat = tickets.filter((t) => ['unlocked', 'checked_in'].includes(t.status) && !t.seat_label);

  // ── Seats ────────────────────────────────────────────────────
  const addSeat = async (e) => {
    e.preventDefault();
    setAddError('');
    try {
      await api.post('/api/admin/seats', {
        event_id:    parseInt(selectedEvent),
        label:       form.label.trim(),
        pos_x:       parseFloat(form.pos_x) || 0,
        pos_y:       parseFloat(form.pos_y) || 0,
        row_name:    form.row_name.trim() || null,
        seat_number: form.seat_number !== '' ? parseInt(form.seat_number) : null,
      });
      setForm({ label: '', pos_x: 0, pos_y: 0, row_name: '', seat_number: '' });
      loadSeats();
    } catch (err) {
      setAddError(err.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const bulkGenerate = async () => {
    if (!bulkRows || !bulkCols) return;
    const rows = parseInt(bulkRows), cols = parseInt(bulkCols), sp = parseInt(bulkSpacing) || 60;
    const seatData = [];
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++) {
        const row = String.fromCharCode(65 + r);
        const seatNumber = c + 1;
        // Reihen sind vertikal angeordnet: jede Reihe ist eine Spalte im Raster,
        // die Sitzplätze innerhalb einer Reihe stapeln sich von oben nach unten.
        seatData.push({ event_id: parseInt(selectedEvent), label: `${row}${seatNumber}`, pos_x: r * sp, pos_y: c * sp, row_name: row, seat_number: seatNumber });
      }
    await api.post('/api/admin/seats/bulk', { event_id: parseInt(selectedEvent), seats: seatData });
    loadSeats();
  };

  const clearAll = async () => {
    if (!window.confirm('Wirklich ALLE Sitzplätze löschen?')) return;
    try { await api.delete(`/api/admin/seats/clear/${selectedEvent}`); loadSeats(); }
    catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteSeat = async (id) => { await api.delete(`/api/admin/seats/${id}`).catch((e) => alert(e.response?.data?.detail)); loadSeats(); };
  const deleteSelected = async () => {
    if (!window.confirm(`${selectedIds.size} Sitzplätze löschen?`)) return;
    for (const id of selectedIds) await api.delete(`/api/admin/seats/${id}`).catch(() => {});
    loadSeats();
  };
  const toggleBlock = async (seat) => { await api.post(`/api/admin/seats/${seat.id}/${seat.status === 'blocked' ? 'unblock' : 'block'}`); loadSeats(); };

  const doReassign = async () => {
    if (!reassign || !reassignTarget) return;
    setReassigning(true);
    try {
      await api.post('/api/admin/seats/reassign', {
        from_seat_id: reassign.id,
        to_seat_id: parseInt(reassignTarget),
      });
      setReassign(null);
      setReassignTarget('');
      loadSeats();
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Verschieben');
    } finally { setReassigning(false); }
  };

  const doAssign = async () => {
    if (!assign || !assignTarget) return;
    setAssigning(true);
    try {
      await api.post(`/api/admin/seats/${assign.id}/assign`, { ticket_id: parseInt(assignTarget) });
      setAssign(null);
      setAssignTarget('');
      loadSeats();
      loadTickets();
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Zuweisen');
    } finally { setAssigning(false); }
  };
  const toggleSelect = (id) => setSelectedIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll   = () => setSelectedIds(new Set(seats.filter((s) => s.status !== 'booked').map((s) => s.id)));
  const deselectAll = () => setSelectedIds(new Set());
  const canDeleteSelected = [...selectedIds].every((id) => { const s = seats.find((s) => s.id === id); return s && s.status !== 'booked'; });

  // ── Markers ──────────────────────────────────────────────────
  const addMarker = async (e) => {
    e.preventDefault();
    const cfg = MARKER_CFG[markerForm.type];
    await api.post('/api/admin/markers', {
      event_id: parseInt(selectedEvent),
      type: markerForm.type,
      label: markerForm.label || null,
      pos_x: 20, pos_y: 20,
      width:  parseFloat(markerForm.width)  || cfg.defaultW,
      height: parseFloat(markerForm.height) || cfg.defaultH,
    });
    setMarkerForm({ type: 'eingang', label: '', width: '', height: '' });
    loadMarkers();
  };

  const deleteMarker = async (id) => { await api.delete(`/api/admin/markers/${id}`); loadMarkers(); };

  // ── Unified drag (seats + markers) ───────────────────────────
  const onSeatMouseDown = (e, seat) => {
    if (seat.status === 'booked') return;
    e.preventDefault();
    const toMove = selectedIds.size > 0 && selectedIds.has(seat.id) ? new Set(selectedIds) : new Set([seat.id]);
    const startPositions = {};
    seats.forEach((s) => { startPositions[s.id] = { x: s.pos_x, y: s.pos_y }; });
    dragState.current = { kind: 'seats', seatId: seat.id, toMove, startX: e.clientX, startY: e.clientY, startPositions, moved: false };
    setIsDragging(false);
  };

  const onMarkerMouseDown = (e, marker) => {
    e.preventDefault();
    const startPositions = { [marker.id]: { x: marker.pos_x, y: marker.pos_y } };
    dragState.current = { kind: 'marker', markerId: marker.id, startX: e.clientX, startY: e.clientY, startPositions, moved: false };
    setIsDragging(false);
  };

  const onMouseMove = (e) => {
    if (!dragState.current) return;
    const ds = dragState.current;
    const dx = e.clientX - ds.startX, dy = e.clientY - ds.startY;
    if (!ds.moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) { ds.moved = true; setIsDragging(true); }
    if (!ds.moved) return;

    if (ds.kind === 'seats') {
      setSeats((p) => p.map((s) => ds.toMove.has(s.id)
        ? { ...s, pos_x: Math.max(0, ds.startPositions[s.id].x + dx), pos_y: Math.max(0, ds.startPositions[s.id].y + dy) }
        : s));
    } else {
      setMarkers((p) => p.map((m) => m.id === ds.markerId
        ? { ...m, pos_x: Math.max(0, ds.startPositions[m.id].x + dx), pos_y: Math.max(0, ds.startPositions[m.id].y + dy) }
        : m));
    }
  };

  const onMouseUp = async () => {
    if (!dragState.current) return;
    const ds = dragState.current;
    if (ds.moved) {
      if (ds.kind === 'seats') {
        const moved = seats.filter((s) => ds.toMove.has(s.id));
        await Promise.all(moved.map((s) => api.put(`/api/admin/seats/${s.id}`, { pos_x: Math.round(s.pos_x), pos_y: Math.round(s.pos_y) })));
      } else {
        const m = markers.find((m) => m.id === ds.markerId);
        if (m) await api.put(`/api/admin/markers/${m.id}`, { pos_x: Math.round(m.pos_x), pos_y: Math.round(m.pos_y) });
      }
    } else if (ds.kind === 'seats') {
      toggleSelect(ds.seatId);
    }
    dragState.current = null;
    setIsDragging(false);
  };

  const maxX = Math.max(...seats.map((s) => s.pos_x), ...markers.map((m) => m.pos_x + m.width), 10) + 80;
  const maxY = Math.max(...seats.map((s) => s.pos_y), ...markers.map((m) => m.pos_y + m.height), 10) + 80;

  return (
    <div>
      <h2 className="page-title">Sitzplanverwaltung</h2>

      <FormField label="Event auswählen" className="max-w-sm">
        <Select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)}>
          <option value="">– Event wählen –</option>
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </Select>
      </FormField>

      {selectedEvent && (
        <>
          {/* Tab-Leiste */}
          <div className="flex border-b border-slate-200 mb-5 gap-0">
            {[['seats', 'Sitzplätze'], ['markers', 'Orientierung']].map(([k, l]) => (
              <button key={k} onClick={() => setActiveTab(k)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === k ? 'border-ocean-600 text-ocean-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}>
                {l}
                {k === 'markers' && markers.length > 0 && (
                  <span className="ml-1.5 text-xs bg-ocean-100 text-ocean-600 px-1.5 py-0.5 rounded font-bold">{markers.length}</span>
                )}
              </button>
            ))}
          </div>

          {/* ── SITZPLÄTZE ── */}
          {activeTab === 'seats' && (
            <>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="card">
                  <h3 className="section-title">Bulk-Generierung</h3>
                  <div className="flex gap-3 flex-wrap items-end">
                    <FormField label="Reihen" className="mb-0"><Input type="number" min="1" value={bulkRows} onChange={(e) => setBulkRows(e.target.value)} className="w-20" /></FormField>
                    <FormField label="Sitzplätze pro Reihe" className="mb-0"><Input type="number" min="1" value={bulkCols} onChange={(e) => setBulkCols(e.target.value)} className="w-20" /></FormField>
                    <FormField label="Abstand (px)" className="mb-0"><Input type="number" value={bulkSpacing} onChange={(e) => setBulkSpacing(e.target.value)} className="w-20" /></FormField>
                    <Button onClick={bulkGenerate} size="sm">Generieren</Button>
                  </div>
                </div>
                <div className="card">
                  <h3 className="section-title">Einzelner Sitz</h3>
                  <form onSubmit={addSeat} className="flex gap-2 flex-wrap items-end">
                    <FormField label="Label" className="mb-0"><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="A1" required className="w-16" /></FormField>
                    <FormField label="X" className="mb-0"><Input type="number" value={form.pos_x} onChange={(e) => setForm({ ...form, pos_x: e.target.value })} className="w-16" /></FormField>
                    <FormField label="Y" className="mb-0"><Input type="number" value={form.pos_y} onChange={(e) => setForm({ ...form, pos_y: e.target.value })} className="w-16" /></FormField>
                    <Button type="submit" size="sm">Hinzufügen</Button>
                  </form>
                  {addError && <p className="text-xs text-red-600 mt-2">{addError}</p>}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 items-center mb-3">
                <span className="text-sm text-slate-500">
                  {seats.length} Sitzplätze · {markers.length} Marker
                  {ticketsWithoutSeat.length > 0 && <span className="text-amber-600 font-medium"> · {ticketsWithoutSeat.length} ohne Sitzplatz</span>}
                </span>
                <div className="flex gap-2 ml-auto flex-wrap">
                  {selectedIds.size > 0 ? (
                    <>
                      <span className="text-sm font-semibold text-ocean-700 bg-ocean-50 px-3 py-1.5 rounded border border-ocean-200">{selectedIds.size} ausgewählt</span>
                      {canDeleteSelected && <Button variant="danger" size="sm" onClick={deleteSelected}>Auswahl löschen</Button>}
                      <Button variant="ghost" size="sm" onClick={deselectAll}>✕ Aufheben</Button>
                    </>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={selectAll}>Alle auswählen</Button>
                  )}
                  <Button variant="danger" size="sm" onClick={clearAll}>Sitzplan löschen</Button>
                </div>
              </div>

              {/* Legende */}
              <div className="flex gap-4 flex-wrap mb-2">
                {Object.entries({ free: 'Frei', reserved: 'Reserviert', booked: 'Gebucht', blocked: 'Gesperrt' }).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1.5 text-xs text-slate-600">
                    <div className="w-3.5 h-3.5 rounded" style={{ background: STATUS_BG[k], border: `1.5px solid ${STATUS_BORDER[k]}` }} />{v}
                  </div>
                ))}
                <div className="flex items-center gap-1.5 text-xs text-slate-600">
                  <div className="w-3.5 h-3.5 rounded bg-ocean-500 ring-2 ring-ocean-300" />Ausgewählt
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-2">Klick = auswählen · Drag = verschieben</p>

              {/* Canvas */}
              <div className="card overflow-auto mb-4 p-3">
                <Canvas seats={seats} markers={markers} maxX={maxX} maxY={maxY} isDragging={isDragging}
                  selectedIds={selectedIds} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
                  onSeatMouseDown={onSeatMouseDown} onMarkerMouseDown={onMarkerMouseDown} />
              </div>

              {/* Seat list */}
              <div className="space-y-2">
                {seats.map((seat) => (
                  <div key={seat.id} className={`card py-3 flex justify-between items-center flex-wrap gap-2 ${selectedIds.has(seat.id) ? 'bg-ocean-50 border-ocean-200' : ''}`}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={selectedIds.has(seat.id)} disabled={seat.status === 'booked'}
                        onChange={() => toggleSelect(seat.id)} className="w-4 h-4 accent-ocean-500 disabled:cursor-not-allowed" />
                      <span className="font-mono font-bold text-ocean-800 w-10">{seat.label}</span>
                      <Badge status={seat.status} />
                      {seat.booked_by_username && (
                        <span className="text-xs text-slate-600 font-medium">{seat.booked_by_username}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {seat.status === 'booked' && (
                        <Button variant="secondary" size="sm" onClick={() => { setReassign(seat); setReassignTarget(''); }}>
                          Platz tauschen
                        </Button>
                      )}
                      {seat.status === 'free' && ticketsWithoutSeat.length > 0 && (
                        <Button variant="secondary" size="sm" onClick={() => { setAssign(seat); setAssignTarget(''); }}>
                          Zuweisen
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => toggleBlock(seat)}>{seat.status === 'blocked' ? 'Entsperren' : 'Sperren'}</Button>
                      {seat.status === 'free' && <Button variant="danger" size="sm" onClick={() => deleteSeat(seat.id)}>Löschen</Button>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reassign Modal */}
              {reassign && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/40" onClick={() => setReassign(null)} />
                  <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
                    <h3 className="font-bold text-lg text-slate-800 mb-1">Sitzplatz tauschen</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      <span className="font-semibold text-ocean-700">{reassign.booked_by_username}</span>{' '}
                      sitzt aktuell auf <span className="font-mono font-bold">{reassign.label}</span>.
                    </p>
                    <label className="label mb-1 block">Neuer Sitzplatz</label>
                    <select
                      value={reassignTarget}
                      onChange={(e) => setReassignTarget(e.target.value)}
                      className="input mb-4 w-full"
                    >
                      <option value="">– Freien Platz wählen –</option>
                      {seats
                        .filter((s) => s.status === 'free')
                        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
                        .map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                    </select>
                    <div className="flex gap-3">
                      <Button onClick={doReassign} loading={reassigning} disabled={!reassignTarget}>
                        Verschieben
                      </Button>
                      <Button variant="ghost" onClick={() => setReassign(null)}>Abbrechen</Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Assign Modal */}
              {assign && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/40" onClick={() => setAssign(null)} />
                  <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
                    <h3 className="font-bold text-lg text-slate-800 mb-1">Sitzplatz zuweisen</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Weise Sitzplatz <span className="font-mono font-bold">{assign.label}</span>{' '}
                      einem Teilnehmer ohne Sitzplatz zu.
                    </p>
                    <label className="label mb-1 block">Teilnehmer</label>
                    <select
                      value={assignTarget}
                      onChange={(e) => setAssignTarget(e.target.value)}
                      className="input mb-4 w-full"
                    >
                      <option value="">– Teilnehmer wählen –</option>
                      {ticketsWithoutSeat.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.buyer ? `${t.buyer.firstname} ${t.buyer.lastname}${t.buyer.nickname ? ` (${t.buyer.nickname})` : ''}` : t.ticket_code}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-3">
                      <Button onClick={doAssign} loading={assigning} disabled={!assignTarget}>
                        Zuweisen
                      </Button>
                      <Button variant="ghost" onClick={() => setAssign(null)}>Abbrechen</Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── ORIENTIERUNGSMARKER ── */}
          {activeTab === 'markers' && (
            <>
              <div className="card mb-5">
                <h3 className="section-title">Marker hinzufügen</h3>
                <form onSubmit={addMarker} className="grid sm:grid-cols-2 gap-x-4">
                  <FormField label="Typ" required>
                    <Select value={markerForm.type} onChange={(e) => {
                      const cfg = MARKER_CFG[e.target.value];
                      setMarkerForm({ ...markerForm, type: e.target.value, width: cfg.defaultW, height: cfg.defaultH });
                    }}>
                      {MARKER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </Select>
                  </FormField>
                  <FormField label="Beschriftung (optional)">
                    <Input value={markerForm.label} onChange={(e) => setMarkerForm({ ...markerForm, label: e.target.value })}
                      placeholder={`z.B. "Haupteingang"`} />
                  </FormField>
                  <FormField label="Breite (px)">
                    <Input type="number" value={markerForm.width} onChange={(e) => setMarkerForm({ ...markerForm, width: e.target.value })} className="w-24" />
                  </FormField>
                  <FormField label="Höhe (px)">
                    <Input type="number" value={markerForm.height} onChange={(e) => setMarkerForm({ ...markerForm, height: e.target.value })} className="w-24" />
                  </FormField>
                  <div className="sm:col-span-2">
                    <Button type="submit" size="sm">Marker platzieren</Button>
                    <p className="text-xs text-slate-400 mt-1">Der Marker wird links oben platziert — per Drag verschieben.</p>
                  </div>
                </form>
              </div>

              {/* Canvas (marker-only drag) */}
              <div className="card overflow-auto mb-4 p-3">
                <Canvas seats={seats} markers={markers} maxX={maxX} maxY={maxY} isDragging={isDragging}
                  selectedIds={selectedIds} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
                  onSeatMouseDown={onSeatMouseDown} onMarkerMouseDown={onMarkerMouseDown} />
              </div>

              {/* Marker list */}
              {markers.length === 0 ? (
                <div className="card text-center py-8 text-slate-400 text-sm">Noch keine Marker gesetzt.</div>
              ) : (
                <div className="space-y-2">
                  {markers.map((m) => {
                    const cfg = MARKER_CFG[m.type];
                    return (
                      <div key={m.id} className="card py-3 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <span style={{ fontSize: '1.2rem' }}>{cfg.icon}</span>
                          <div>
                            <span className="font-medium text-slate-800">{m.label || cfg.label}</span>
                            <span className="text-xs text-slate-400 ml-2">({m.type})</span>
                          </div>
                          <span className="text-xs text-slate-400">{Math.round(m.width)} × {Math.round(m.height)} px</span>
                        </div>
                        <Button variant="danger" size="sm" onClick={() => deleteMarker(m.id)}>Löschen</Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// Shared canvas component
function Canvas({ seats, markers, maxX, maxY, isDragging, selectedIds, onMouseMove, onMouseUp, onSeatMouseDown, onMarkerMouseDown }) {
  const STATUS_BG     = { free: '#d1fae5', reserved: '#fef3c7', booked: '#dbeafe', blocked: '#fee2e2' };
  const STATUS_BORDER = { free: '#34d399', reserved: '#fbbf24', booked: '#60a5fa', blocked: '#f87171' };
  const STATUS_TEXT   = { free: '#065f46', reserved: '#92400e', booked: '#1e3a8a', blocked: '#991b1b' };

  return (
    <div className="relative select-none"
      style={{ width: maxX, height: maxY, minWidth: 300, minHeight: 200, cursor: isDragging ? 'grabbing' : 'default' }}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}>

      {/* Markers (behind seats) */}
      {markers.map((m) => (
        <SeatMapMarkerEl key={`m-${m.id}`} marker={m} dragging={isDragging} onMouseDown={onMarkerMouseDown} />
      ))}

      {/* Seats */}
      {seats.map((seat) => {
        const isSelected = selectedIds.has(seat.id);
        const isBooked   = seat.status === 'booked';
        return (
          <div key={seat.id} onMouseDown={(e) => !isBooked && onSeatMouseDown(e, seat)}
            title={`${seat.label}${seat.booked_by_username ? ` — ${seat.booked_by_username}` : ''}`}
            style={{
              position: 'absolute', left: Math.round(seat.pos_x), top: Math.round(seat.pos_y),
              width: 44, height: 44, borderRadius: 6,
              background: isSelected ? '#1A91D1' : STATUS_BG[seat.status] || '#f1f5f9',
              border: `2px solid ${isSelected ? '#0D5386' : STATUS_BORDER[seat.status] || '#94a3b8'}`,
              boxShadow: isSelected ? '0 0 0 3px #43B3E844' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.7rem', fontWeight: 700,
              color: isSelected ? '#fff' : STATUS_TEXT[seat.status] || '#334155',
              cursor: isBooked ? 'default' : isDragging ? 'grabbing' : 'pointer',
              transition: isDragging ? 'none' : 'box-shadow 0.1s',
              zIndex: isSelected ? 10 : 2,
            }}>
            {isSelected && (
              <div style={{ position: 'absolute', top: -6, right: -6, width: 14, height: 14, borderRadius: '50%', background: '#0D5386', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 4l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
            )}
            {seat.label}
          </div>
        );
      })}
    </div>
  );
}
